import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Location } from 'history';
import { useSnackbar } from 'notistack';
import Papa from 'papaparse';
import fileDownload from 'react-file-download';
import { useHistory } from 'react-router-dom';

import { LoadingButton } from '@mui/lab';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import {
  GridCellModes,
  GridCellModesModel,
  GridCellParams,
  GridColDef,
  GridRenderCellParams,
  GridRowModel,
} from '@mui/x-data-grid-pro';

import PageHeader from '~/components/Common/Elements/PageHeader';
import StyledDataGrid from '~/components/CustomerCommunication/HeroPlanner/Components/ScheduleForms/StyledDataGrid';

import useDataGridPagination from '~/hooks/mui/useDataGridPagination';

import {
  FareFamilyPricingGetRes,
  fetchCharterFareFamilyPricing,
  previewCharterFareFamilyPricing,
  updateCharterFareFamilyPricing,
} from '~/services/CharterService';
import { formatDateISO } from '~/services/TimeService';

import { charterFlightsPricingColumnGroupingModel, charterFlightsPricingColumns } from './CharterFlightsPricingColumns';
import {
  DEPARTING_ARR_AIRPORT,
  DEPARTING_DEP_AIRPORT,
  RETURNING_ARR_AIRPORT,
  RETURNING_DEP_AIRPORT,
  buildPreviewPayloadFromRows,
  buildUpdatePayload,
  generateCsvDataFromPreview,
  mapApiResultToRow,
  parseAndValidateRow,
} from './CharterFlightsPricingHelpers';
import CharterFlightsPricingToolbar from './CharterFlightsPricingToolbar';
import type { CharterPricingRow, FareFamilyPricingResult, PageInfo } from './CharterFlightsPricingTypes';

export type { CharterPricingRow } from './CharterFlightsPricingTypes';

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

export default function CharterFlightsPricingPage() {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const history = useHistory();
  const { paginationModel, setPaginationModel } = useDataGridPagination({ page: 0, pageSize: 50 });
  const [isViewMode, setIsViewMode] = useState(true);
  const [isDiscardDialogOpen, setIsDiscardDialogOpen] = useState(false);
  const [editedRows, setEditedRows] = useState<Map<string, CharterPricingRow>>(new Map());
  const hasUnsavedChanges = editedRows.size > 0;
  const editedRowPagesRef = useRef<Map<string, PageInfo>>(new Map());
  const [cellModesModel, setCellModesModel] = useState<GridCellModesModel>({});

  // Navigation blocking state
  const [pendingNavigation, setPendingNavigation] = useState<Location | null>(null);
  const unblockRef = useRef<(() => void) | null>(null);

  const offset = paginationModel.page * paginationModel.pageSize;
  const limit = paginationModel.pageSize;

  // Block navigation when there are unsaved changes
  useEffect(() => {
    if (hasUnsavedChanges) {
      unblockRef.current = history.block((location) => {
        setPendingNavigation(location);
        setIsDiscardDialogOpen(true);
        // Return false to prevent navigation
        return false;
      });
    } else {
      if (unblockRef.current) {
        unblockRef.current();
        unblockRef.current = null;
      }
    }

    return () => {
      if (unblockRef.current) {
        unblockRef.current();
        unblockRef.current = null;
      }
    };
  }, [hasUnsavedChanges, history]);

  // Block browser refresh/close when there are unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        event.preventDefault();
        // Modern browsers require returnValue to be set
        event.returnValue = '';
        return '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [hasUnsavedChanges]);

  const {
    data: pricingData,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    refetchOnWindowFocus: false,
    queryKey: [
      'charterFareFamilyPricing',
      DEPARTING_DEP_AIRPORT,
      DEPARTING_ARR_AIRPORT,
      RETURNING_DEP_AIRPORT,
      RETURNING_ARR_AIRPORT,
      offset,
      limit,
    ],
    queryFn: async () => {
      return await fetchCharterFareFamilyPricing({
        departingDepAirport: DEPARTING_DEP_AIRPORT,
        departingArrAirport: DEPARTING_ARR_AIRPORT,
        returningDepAirport: RETURNING_DEP_AIRPORT,
        returningArrAirport: RETURNING_ARR_AIRPORT,
        offset,
        limit,
      });
    },
  });

  const updatePricingMutation = useMutation({
    mutationFn: updateCharterFareFamilyPricing,
  });

  const previewPricingMutation = useMutation({
    mutationFn: previewCharterFareFamilyPricing,
  });

  const rows = useMemo<Array<CharterPricingRow>>(() => {
    if (!pricingData?.result) {
      return [];
    }
    return pricingData.result.map((item) => {
      const baseRow = mapApiResultToRow(item);
      return editedRows.get(baseRow.flightInstanceId) ?? baseRow;
    });
  }, [pricingData, editedRows]);

  const totalRowCount = pricingData?.pagination?.total_items ?? 0;

  const handleEnterEditMode = useCallback(() => {
    setIsViewMode(false);
  }, []);

  const handleExitEditMode = useCallback(() => {
    if (hasUnsavedChanges) {
      setIsDiscardDialogOpen(true);
    } else {
      setIsViewMode(true);
    }
  }, [hasUnsavedChanges]);

  const handleConfirmDiscard = useCallback(() => {
    // Clear all edited state
    setEditedRows(new Map());
    editedRowPagesRef.current.clear();
    setCellModesModel({});
    setIsDiscardDialogOpen(false);

    // Unblock navigation before navigating
    if (unblockRef.current) {
      unblockRef.current();
      unblockRef.current = null;
    }

    if (pendingNavigation) {
      // Navigate to the pending location
      const targetPath = pendingNavigation.pathname + pendingNavigation.search + pendingNavigation.hash;
      setPendingNavigation(null);
      history.push(targetPath);
    } else {
      // Just exiting edit mode without navigation
      setIsViewMode(true);
    }
  }, [pendingNavigation, history]);

  const handleCancelDiscard = useCallback(() => {
    setIsDiscardDialogOpen(false);
    setPendingNavigation(null);
  }, []);

  const processRowUpdate = useCallback(
    (newRow: GridRowModel<CharterPricingRow>, oldRow: GridRowModel<CharterPricingRow>) => {
      const validatedRow = parseAndValidateRow(newRow);

      // Check if any editable field actually changed
      const hasActualChanges = (Object.keys(validatedRow) as Array<keyof CharterPricingRow>).some((key) => {
        return validatedRow[key] !== oldRow[key];
      });

      if (hasActualChanges) {
        setEditedRows((prev) => {
          const updated = new Map(prev);
          updated.set(validatedRow.flightInstanceId, validatedRow);
          return updated;
        });
        editedRowPagesRef.current.set(validatedRow.flightInstanceId, { offset, limit });
      }

      return validatedRow;
    },
    [offset, limit],
  );

  const handleProcessRowUpdateError = useCallback(
    (err: Error) => {
      enqueueSnackbar(`Invalid value: ${err.message}`, { variant: 'error' });
    },
    [enqueueSnackbar],
  );

  // Single-click editing: enter edit mode on cell click when not in view mode
  const handleCellClick = useCallback(
    (params: GridCellParams, event: React.MouseEvent) => {
      if (isViewMode || !params.isEditable) {
        return;
      }

      // Ignore portal clicks
      if ((event.target as HTMLElement).nodeType === 1 && !event.currentTarget.contains(event.target as Element)) {
        return;
      }

      setCellModesModel((prevModel) => {
        return {
          // Revert the mode of cells from other rows to view
          ...Object.keys(prevModel).reduce(
            (acc, id) => ({
              ...acc,
              [id]: Object.keys(prevModel[id]).reduce(
                (acc2, field) => ({
                  ...acc2,
                  [field]: { mode: GridCellModes.View },
                }),
                {},
              ),
            }),
            {},
          ),
          [params.id]: {
            // Revert the mode of other cells in the same row to view
            ...Object.keys(prevModel[params.id] || {}).reduce(
              (acc, field) => ({ ...acc, [field]: { mode: GridCellModes.View } }),
              {},
            ),
            [params.field]: { mode: GridCellModes.Edit },
          },
        };
      });
    },
    [isViewMode],
  );

  const handleCellModesModelChange = useCallback((newModel: GridCellModesModel) => {
    setCellModesModel(newModel);
  }, []);

  const handleToggleChildEconomyFree = useCallback(
    (rowId: string, currentValue: boolean) => {
      setEditedRows((prev) => {
        const baseRow = pricingData?.result?.map(mapApiResultToRow).find((r) => r.id === rowId);
        const row = prev.get(rowId) ?? baseRow;
        if (!row) {
          return prev;
        }
        const updatedRow: CharterPricingRow = { ...row, packagedChildEconomyFree: !currentValue };
        const updated = new Map(prev);
        updated.set(updatedRow.flightInstanceId, updatedRow);
        return updated;
      });
      editedRowPagesRef.current.set(rowId, { offset, limit });
    },
    [pricingData, offset, limit],
  );

  const columns = useMemo<Array<GridColDef<CharterPricingRow>>>(() => {
    return charterFlightsPricingColumns.map((col) => {
      if (col.field !== 'packagedChildEconomyFree') {
        return col;
      }
      return {
        ...col,
        renderCell: (params: GridRenderCellParams<CharterPricingRow, boolean>) => (
          <Checkbox
            checked={!!params.value}
            disabled={isViewMode}
            onChange={() => handleToggleChildEconomyFree(String(params.id), !!params.value)}
            size="small"
          />
        ),
      };
    });
  }, [isViewMode, handleToggleChildEconomyFree]);

  const handleSave = useCallback(async () => {
    if (editedRows.size === 0) {
      enqueueSnackbar('No changes to save', { variant: 'warning' });
      return;
    }

    const payload = buildUpdatePayload(editedRows);

    try {
      await updatePricingMutation.mutateAsync(payload);

      // Invalidate only the pages that had edited rows
      const uniquePages = new Map<string, PageInfo>();
      for (const [flightInstanceId] of editedRows) {
        const pageInfo = editedRowPagesRef.current.get(flightInstanceId);
        if (pageInfo) {
          const key = `${pageInfo.offset}|${pageInfo.limit}`;
          uniquePages.set(key, pageInfo);
        }
      }

      await Promise.all(
        Array.from(uniquePages.values()).map((pageInfo) =>
          queryClient.invalidateQueries({
            queryKey: [
              'charterFareFamilyPricing',
              DEPARTING_DEP_AIRPORT,
              DEPARTING_ARR_AIRPORT,
              RETURNING_DEP_AIRPORT,
              RETURNING_ARR_AIRPORT,
              pageInfo.offset,
              pageInfo.limit,
            ],
            refetchType: 'active',
          }),
        ),
      );

      enqueueSnackbar(`Changes saved successfully (${editedRows.size} rows updated)`, { variant: 'success' });
      setEditedRows(new Map());
      editedRowPagesRef.current.clear();
      setCellModesModel({});
      setIsViewMode(true);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An unexpected error occurred';
      enqueueSnackbar(`Failed to save changes: ${errorMessage}`, { variant: 'error' });
    }
  }, [enqueueSnackbar, editedRows, queryClient, updatePricingMutation]);

  const handleDownloadCsv = useCallback(async () => {
    try {
      const cachedQueries = queryClient.getQueriesData<FareFamilyPricingGetRes>({
        queryKey: [
          'charterFareFamilyPricing',
          DEPARTING_DEP_AIRPORT,
          DEPARTING_ARR_AIRPORT,
          RETURNING_DEP_AIRPORT,
          RETURNING_ARR_AIRPORT,
        ],
      });

      const allResults: Array<FareFamilyPricingResult> = [];
      for (const [, data] of cachedQueries) {
        if (data?.result) {
          allResults.push(...data.result);
        }
      }

      // De-duplicate in case of overlapping cache entries
      const uniqueResultsMap = new Map<string, FareFamilyPricingResult>();
      for (const result of allResults) {
        uniqueResultsMap.set(result.flightInstanceId, result);
      }
      const allApiResults = Array.from(uniqueResultsMap.values());

      if (allApiResults.length === 0) {
        enqueueSnackbar('No pricing data found in cache', { variant: 'warning' });
        return;
      }

      // Convert API results to rows and merge with edited rows (pending changes)
      const allRows = allApiResults.map((item) => {
        const baseRow = mapApiResultToRow(item);
        // Apply edited row if it exists, otherwise use the base row from API
        return editedRows.get(baseRow.flightInstanceId) ?? baseRow;
      });

      const previewPayload = buildPreviewPayloadFromRows(allRows);
      if (previewPayload.items.length === 0) {
        enqueueSnackbar('No valid departure/return pairs found for CSV export', { variant: 'warning' });
        return;
      }

      const previewResponse = await previewPricingMutation.mutateAsync(previewPayload);
      const csvData = generateCsvDataFromPreview(previewResponse.result);

      if (csvData.length <= 4) {
        enqueueSnackbar('No data rows in preview response', { variant: 'warning' });
        return;
      }

      const csv = Papa.unparse(csvData, { header: false });
      const fileName = `charter-flights-pricing-${formatDateISO()}.csv`;
      fileDownload(csv, fileName);

      enqueueSnackbar(`CSV exported successfully with ${csvData.length - 4} rows`, { variant: 'success' });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An unexpected error occurred';
      enqueueSnackbar(`Failed to generate CSV: ${errorMessage}`, { variant: 'error' });
    }
  }, [queryClient, enqueueSnackbar, previewPricingMutation, editedRows]);

  const hasData = !isLoading && !isError;

  return (
    <Container maxWidth="xl" sx={hasData ? { width: 'fit-content' } : undefined}>
      <PageHeader title="Charter Flights Pricing" />

      {isLoading && (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
          <Typography variant="body1" sx={{ ml: 2 }}>
            Loading pricing data...
          </Typography>
        </Box>
      )}

      {isError && (
        <Box mt={4}>
          <Alert
            severity="error"
            action={
              <Button color="inherit" variant="outlined" onClick={() => refetch()}>
                Retry
              </Button>
            }
          >
            <AlertTitle>Failed to load pricing data</AlertTitle>
            {error instanceof Error ? error.message : 'An unexpected error occurred'}
          </Alert>
        </Box>
      )}

      {hasData && (
        <>
          <Stack direction="row" justifyContent="flex-start" alignItems="center" mb={3}>
            <Stack direction="row" spacing={2}>
              <LoadingButton variant="outlined" onClick={handleDownloadCsv} loading={previewPricingMutation.isPending}>
                Download CSV
              </LoadingButton>
              <Button variant="outlined" onClick={isViewMode ? handleEnterEditMode : handleExitEditMode}>
                {isViewMode ? 'Edit Pricing' : 'View Mode'}
              </Button>
              {!isViewMode && (
                <LoadingButton
                  variant="contained"
                  color="primary"
                  onClick={handleSave}
                  loading={updatePricingMutation.isPending}
                  disabled={updatePricingMutation.isPending}
                >
                  Save Changes
                </LoadingButton>
              )}
            </Stack>
          </Stack>

          <Box sx={{ width: '100%' }}>
            <StyledDataGrid
              rows={rows}
              columns={columns}
              columnGroupingModel={charterFlightsPricingColumnGroupingModel}
              columnHeaderHeight={72}
              initialState={{
                sorting: {
                  sortModel: [{ field: 'departureDate', sort: 'asc' }],
                },
              }}
              pagination
              paginationMode="server"
              paginationModel={paginationModel}
              onPaginationModelChange={setPaginationModel}
              pageSizeOptions={PAGE_SIZE_OPTIONS}
              rowCount={totalRowCount}
              editMode="cell"
              isCellEditable={() => !isViewMode}
              cellModesModel={cellModesModel}
              onCellModesModelChange={handleCellModesModelChange}
              onCellClick={handleCellClick}
              processRowUpdate={processRowUpdate}
              onProcessRowUpdateError={handleProcessRowUpdateError}
              slots={{
                toolbar: CharterFlightsPricingToolbar,
              }}
              sx={{
                '& .MuiDataGrid-cell[data-field="separator"]': {
                  backgroundColor: 'action.hover',
                  borderLeft: '1px solid',
                  borderRight: '1px solid',
                  borderColor: 'divider',
                },
                maxHeight: '1000px',
                // Edit mode styling: make editable cells look like input fields
                // Target editable columns by data-field attribute (packaged* and standalone* fields)
                ...(!isViewMode && {
                  '& .MuiDataGrid-cell[data-field^="packaged"]:not([data-field="packagedChildEconomyFree"]), & .MuiDataGrid-cell[data-field^="standalone"]':
                    {
                      outline: '1px solid',
                      outlineColor: 'grey.400',
                      outlineOffset: -1,
                      borderRadius: 1,
                      backgroundColor: 'background.paper',
                      '&:hover': {
                        outlineColor: 'primary.main',
                      },
                    },
                  '& .MuiDataGrid-cell--editing': {
                    outline: '2px solid',
                    outlineColor: 'primary.main',
                    outlineOffset: -2,
                    boxShadow: 1,
                    backgroundColor: 'background.paper',
                  },
                }),
              }}
              disableRowSelectionOnClick
            />
          </Box>
        </>
      )}

      <Dialog open={isDiscardDialogOpen} onClose={handleCancelDiscard}>
        <DialogTitle>Unsaved Changes</DialogTitle>
        <DialogContent>
          <DialogContentText>
            You have unsaved pricing changes.{' '}
            {pendingNavigation
              ? 'If you leave this page, they will be discarded.'
              : 'If you leave Edit Mode, they will be discarded.'}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleConfirmDiscard} variant="text">
            Discard changes
          </Button>
          <Button onClick={handleCancelDiscard} variant="contained" color="primary">
            Keep editing
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
